import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import AdminNavbar from "../components/AdminNavbar";
import "./AdminOrders.css";

const formatShippingAddress = (address) => {
  if (!address) return "-";

  const parts = [
    address.street,
    `${address.city || ""}${address.state ? `, ${address.state}` : ""}${address.postalCode ? ` ${address.postalCode}` : ""}`.trim(),
    address.country,
  ].filter(Boolean);

  return parts.join("\n");
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusDrafts, setStatusDrafts] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Orders | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Manage order operations on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  const statusOptions = [
    "pending",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
  ];

  const fetchOrders = async () => {
    try {
      const data = await api.getAdminOrders();
      const nextOrders = data.data || [];
      setOrders(nextOrders);
      setStatusDrafts((current) => {
        const nextDrafts = { ...current };
        nextOrders.forEach((order) => {
          if (!nextDrafts[order._id]) {
            nextDrafts[order._id] = order.status || "pending";
          }
        });
        return nextDrafts;
      });
    } catch (error) {
      console.error("Failed to load orders:", error);
      alert("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (id, currentStatus) => {
    const nextStatus = statusDrafts[id];
    if (!nextStatus) return;

    if (nextStatus === currentStatus) {
      alert("Status is unchanged");
      return;
    }

    const confirmed = window.confirm(
      `Change order status from ${currentStatus} to ${nextStatus}?`,
    );
    if (!confirmed) return;

    setUpdatingId(id);
    try {
      const response = await api.updateAdminOrderStatus(id, nextStatus);

      if (!response?.success) {
        alert(response?.message || "Failed to update order status");
        return;
      }

      await fetchOrders();
    } catch (error) {
      console.error("Failed to update order status:", error);
      alert(error.message || "Failed to update order status");
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteOrder = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this order?",
    );
    if (!confirmed) return;

    setDeletingId(id);
    try {
      await api.deleteAdminOrder(id);
      await fetchOrders();
    } catch (error) {
      console.error("Failed to delete order:", error);
      alert("Failed to delete order");
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "ao-status--delivered";
      case "shipped":
        return "ao-status--shipped";
      case "processing":
        return "ao-status--processing";
      case "cancelled":
        return "ao-status--cancelled";
      case "pending":
      default:
        return "ao-status--pending";
    }
  };

  const formatOrderId = (id) => {
    if (!id) return "-";
    return `#${id.slice(-6).toUpperCase()}`;
  };

  return (
    <div className="ao-page">
      <AdminNavbar />

      <div className="ao-container">
        {/* Header */}
        <div className="ao-header">
          <div className="ao-header-content">
            <h1>Order Management</h1>
            <p>Track, update, and manage all customer orders</p>
          </div>
          <div className="ao-header-stats">
            <div className="ao-stat">
              <span className="ao-stat-value">{orders.length}</span>
              <span className="ao-stat-label">Total Orders</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="ao-table-card">
          {loading ? (
            <div className="ao-loading">
              <div className="ao-spinner"></div>
              <p>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="ao-empty">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <h3>No orders yet</h3>
              <p>Orders will appear here once customers start purchasing</p>
            </div>
          ) : (
            <div className="ao-table-wrap">
              <table className="ao-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Buyer</th>
                    <th>Items</th>
                    <th>Shipping</th>
                    <th>Payment</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr
                      key={o._id}
                      className="ao-row-clickable"
                      onClick={() => navigate(`/admin/orders/${o._id}`)}
                    >
                      <td>
                        <div className="ao-order-cell">
                          <span className="ao-order-id">
                            {formatOrderId(o._id)}
                          </span>
                          <span className="ao-order-date">
                            {o.createdAt
                              ? new Date(o.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="ao-buyer-cell">
                          <div className="ao-buyer-avatar">
                            {(o.user?.username || "U").charAt(0).toUpperCase()}
                          </div>
                          <div className="ao-buyer-info">
                            <span className="ao-buyer-name">
                              {o.user?.username || "Unknown"}
                            </span>
                            <span className="ao-buyer-email">
                              {o.user?.email || "-"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="ao-items-cell">
                          <span className="ao-items-count">
                            {o.itemCount || o.items?.length || 0} item
                            {(o.itemCount || o.items?.length || 0) !== 1
                              ? "s"
                              : ""}
                          </span>
                          <span className="ao-items-names">
                            {Array.isArray(o.items) && o.items.length > 0
                              ? o.items
                                  .slice(0, 2)
                                  .map((item) => item.name)
                                  .join(", ") +
                                (o.items.length > 2 ? "..." : "")
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="ao-shipping-cell">
                          {formatShippingAddress(o.shippingAddress)}
                        </div>
                      </td>
                      <td>
                        <span className="ao-payment">
                          {String(o.paymentMethod || "-").replace("_", " ")}
                        </span>
                      </td>
                      <td>
                        <span className="ao-total">
                          BND {Number(o.total || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <div className="ao-status-cell">
                          <span
                            className={`ao-status-badge ${getStatusBadgeClass(o.status)}`}
                          >
                            {o.status || "pending"}
                          </span>
                          <div className="ao-status-select-wrap">
                            <select
                              className="ao-status-select"
                              value={
                                statusDrafts[o._id] || o.status || "pending"
                              }
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                setStatusDrafts((current) => ({
                                  ...current,
                                  [o._id]: e.target.value,
                                }))
                              }
                            >
                              {statusOptions.map((status) => (
                                <option key={status} value={status}>
                                  {status.charAt(0).toUpperCase() +
                                    status.slice(1)}
                                </option>
                              ))}
                            </select>
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="ao-actions">
                          <button
                            className="ao-btn ao-btn-update"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateStatus(o._id, o.status || "pending");
                            }}
                            disabled={updatingId === o._id}
                          >
                            {updatingId === o._id ? (
                              <div className="ao-btn-spinner"></div>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                            <span>Update</span>
                          </button>
                          <button
                            className="ao-btn ao-btn-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteOrder(o._id);
                            }}
                            disabled={deletingId === o._id}
                          >
                            {deletingId === o._id ? (
                              <div className="ao-btn-spinner delete"></div>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
